import React, { useState, useEffect, useMemo } from "react";
import "../../styles/components/event-creation-form.css";
import { formatScheduleDate } from "../../logic/formatDate";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { EditableEventSchema } from "../../data/validationSchemas/EventSchema";

const durations = {
  "1 hour": 60,
  "90 minutes": 90,
  "2 hours": 120,
};

const emptyEvent = {
  eventName: "",
  description: "",
  location: "",
  startDate: "",
  startTime: "",
  endTime: "",
  eventType: "",
  gatheringID: "",
  eventParticipants: [],
  childFriendly: false,
};

const generateDatesBetween = (start, end, includeDate) => {
  const dates = [];

  if (!start || !end) return dates;

  const currentDate = new Date(start);
  const endDateObj = new Date(end);

  while (currentDate <= endDateObj) {
    dates.push(currentDate.toISOString().split("T")[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  if (includeDate && !dates.includes(includeDate)) {
    dates.push(includeDate);
    dates.sort();
  }

  return dates;
};

const EventCreationForm = ({
  handleAddEventToSchedule,
  locations,
  eventTypes,
  startDate,
  endDate,
  initialEvent,
}) => {
  const [currentEvent, setCurrentEvent] = useState(emptyEvent);
  const [duration, setDuration] = useState("1 hour");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeValues, setTimeValues] = useState({
    start: null,
    end: null,
  });
  const dates = useMemo(
    () =>
      generateDatesBetween(
        startDate,
        endDate,
        initialEvent?.startDate || null,
      ),
    [startDate, endDate, initialEvent?.startDate],
  );

  useEffect(() => {
    if (initialEvent) {
      const startTime = initialEvent.startTime ? dayjs(initialEvent.startTime, "HH:mm") : null;
      const endTime = initialEvent.endTime ? dayjs(initialEvent.endTime, "HH:mm") : null;

      setCurrentEvent({
        id: initialEvent.id,
        eventName: initialEvent.eventName || "",
        description: initialEvent.description || "",
        location: initialEvent.location || "",
        startDate: initialEvent.startDate || "",
        startTime: initialEvent.startTime || "",
        endTime: initialEvent.endTime || "",
        eventType: initialEvent.eventType || "",
        gatheringID: initialEvent.gatheringID || initialEvent.GatheringID || "",
        eventParticipants:
          initialEvent.eventParticipants ||
          initialEvent.EventParticipants ||
          [],
        childFriendly:
          initialEvent.childFriendly || initialEvent.ChildFriendly || false,
      });

      setTimeValues({
        start: startTime,
        end: endTime,
      });

      if (initialEvent.startTime && initialEvent.endTime) {
        const [startH, startM] = initialEvent.startTime.split(":").map(Number);
        const [endH, endM] = initialEvent.endTime.split(":").map(Number);
        const diff = endH * 60 + endM - (startH * 60 + startM);
        const found = Object.entries(durations).find(
          ([label, mins]) => mins === diff,
        );
        setDuration(found ? found[0] : "1 hour");
      }
    } else {
      setCurrentEvent(emptyEvent);
      setDuration("1 hour");
      setTimeValues({ start: null, end: null });
    }
  }, [initialEvent]);

  // If no date is selected, default to the first available date
  useEffect(() => {
    if (!currentEvent.startDate && dates.length > 0) {
      setCurrentEvent((prev) => ({ ...prev, startDate: dates[0] }));
    }
  }, [currentEvent.startDate, dates]);

  const formatTimeValue = (timeValue) => {
    if (!timeValue) return "";
    return dayjs(timeValue).format("HH:mm");
  };

  const handleStartTimeChange = (value) => {
    const formattedValue = formatTimeValue(value);
    const durationInMinutes = durations[duration] || 60;
    const calculatedEnd = value ? value.add(durationInMinutes, "minute") : null;

    setTimeValues((prev) => ({
      ...prev,
      start: value,
      end: calculatedEnd,
    }));

    setCurrentEvent((prevEvent) => ({
      ...prevEvent,
      startTime: formattedValue,
      endTime: calculatedEnd ? formatTimeValue(calculatedEnd) : "",
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      startTime: "",
      endTime: "",
    }));
  };

  const handleEndTimeChange = (value) => {
    const formattedValue = formatTimeValue(value);

    setTimeValues((prev) => ({
      ...prev,
      end: value,
    }));

    setCurrentEvent((prevEvent) => ({
      ...prevEvent,
      endTime: formattedValue,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      endTime: "",
    }));
  };

  const adjustStartBy = (minutes) => {
    if (!timeValues.start) return;
    const newVal = timeValues.start.add(minutes, "minute");
    handleStartTimeChange(newVal);
  };

  const adjustEndBy = (minutes) => {
    if (!timeValues.end) return;
    const newVal = timeValues.end.add(minutes, "minute");
    handleEndTimeChange(newVal);
  };

  const applyPreset = (hhmm) => {
    const [h, m] = hhmm.split(":");
    const val = dayjs().hour(Number(h)).minute(Number(m));
    handleStartTimeChange(val);
  };

  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setCurrentEvent((prevEvent) => ({
      ...prevEvent,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  const handleDurationChange = (e) => {
    const selectedDuration = e.target.value;
    setDuration(selectedDuration);

    if (timeValues.start) {
      const durationInMinutes = durations[selectedDuration];
      const calculatedEnd = timeValues.start.add(durationInMinutes, "minute");

      setTimeValues((prev) => ({
        ...prev,
        end: calculatedEnd,
      }));

      setCurrentEvent((prevEvent) => ({
        ...prevEvent,
        endTime: formatTimeValue(calculatedEnd),
      }));
    }
  };

  const validateInputs = () => {
    const result = EditableEventSchema.safeParse(currentEvent);

    if (result.success) {
      setErrors({});
      return result.data;
    }

    const newErrors = {};
    result.error.issues.forEach((issue) => {
      const field = issue.path[0];
      if (field && !newErrors[field]) newErrors[field] = issue.message;
    });

    setErrors(newErrors);
    return null;
  };

  const handleSubmit = async () => {
    const validatedFields = validateInputs();
    if (!validatedFields) return;

    setIsSubmitting(true);

    try {
      await handleAddEventToSchedule({
        ...currentEvent,
        ...validatedFields,
      });

      if (!initialEvent) {
        setCurrentEvent(emptyEvent);
        setDuration("1 hour");
        setTimeValues({ start: null, end: null });
      }
    } catch (error) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        submit:
          error?.issues?.map((issue) => issue.message).join(" ") ||
          error?.message ||
          "The event could not be saved. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="event-input-container">
      <h3>{initialEvent ? "Edit Event" : "Add a New Event"}</h3>

      <div className="form-section">
        <h4>Basic Information</h4>

        <label>Event Name</label>
        <input
          className="event-input-field"
          type="text"
          placeholder="Event Name"
          value={currentEvent.eventName}
          onChange={(e) => handleInputChange(e, "eventName")}
          required
        />
        {errors.eventName && (
          <p className="error-message">{errors.eventName}</p>
        )}

        <label>Description</label>
        <textarea
          className="event-input-field"
          rows="4"
          placeholder="Description"
          value={currentEvent.description}
          onChange={(e) => handleInputChange(e, "description")}
        />
        {errors.description && (
          <p className="error-message">{errors.description}</p>
        )}
      </div>

      <div className="form-section">
        <h4>Time/Date</h4>

        <label>Select Date</label>
        <div className="date-buttons-container">
          {dates.map((date, index) => {
            const isSelected = currentEvent.startDate === date;

            return (
              <button
                type="button"
                key={`${date}-${index}`}
                className={`date-button ${isSelected ? "active" : ""}`}
                aria-pressed={isSelected}
                onClick={() =>
                  handleInputChange({ target: { value: date } }, "startDate")
                }
              >
                {isSelected && <span className="selected-check">✓</span>}
                <span>{formatScheduleDate(date)}</span>
              </button>
            );
          })}
        </div>
        {errors.startDate && (
          <p className="error-message">{errors.startDate}</p>
        )}

        <label id="start-time-label">Select Start Time</label>
        <div className="time-presets" role="group" aria-label="Common start time presets">
          <button type="button" className="preset-button" onClick={() => applyPreset("09:00")}>Morning 9:00</button>
          <button type="button" className="preset-button" onClick={() => applyPreset("12:00")}>Noon 12:00</button>
          <button type="button" className="preset-button" onClick={() => applyPreset("15:00")}>Afternoon 15:00</button>
          <button type="button" className="preset-button" onClick={() => applyPreset("18:00")}>Evening 18:00</button>
        </div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={timeValues.start}
            onChange={handleStartTimeChange}
            ampm={false}
            format="HH:mm"
            slotProps={{ textField: { 'aria-labelledby': 'start-time-label' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="event-input-field"
                placeholder="Start Time"
                error={Boolean(errors.startTime)}
                helperText={errors.startTime}
              />
            )}
          />
        </LocalizationProvider>
        <div className="time-adjust-controls" aria-hidden={false}>
          <button type="button" className="time-adjust-button small" onClick={() => adjustStartBy(-15)} aria-label="Subtract 15 minutes">-15</button>
          <button type="button" className="time-adjust-button small" onClick={() => adjustStartBy(15)} aria-label="Add 15 minutes">+15</button>
          <div className="helper-note">Tip: use presets or +15/-15 to quickly set time.</div>
        </div>

        <label id="duration-label">Select Duration</label>
        <select
          className="event-input-field"
          value={duration}
          onChange={handleDurationChange}
        >
          {Object.keys(durations).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>

        <label id="end-time-label">Select End Time</label>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={timeValues.end}
            onChange={handleEndTimeChange}
            ampm={false}
            format="HH:mm"
            slotProps={{ textField: { 'aria-labelledby': 'end-time-label' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="event-input-field"
                placeholder="End Time"
                error={Boolean(errors.endTime)}
                helperText={errors.endTime}
              />
            )}
          />
        </LocalizationProvider>
        <div className="time-adjust-controls">
          <button type="button" className="time-adjust-button small" onClick={() => adjustEndBy(-15)} aria-label="Subtract 15 minutes from end">-15</button>
          <button type="button" className="time-adjust-button small" onClick={() => adjustEndBy(15)} aria-label="Add 15 minutes to end">+15</button>
        </div>
        <div className="helper-note">If you change the start time, the end time updates automatically based on duration.</div>
      </div>

      <div className="form-section">
        <h4>Location</h4>

        {locations.length > 0 && (
          <div className="location-buttons-container">
            {locations.map((location) => {
              const isSelected = currentEvent.location === location;

              return (
                <button
                  type="button"
                  key={location}
                  className={`location-button ${isSelected ? "active" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    handleInputChange(
                      { target: { value: location } },
                      "location",
                    )
                  }
                >
                  {isSelected && <span className="selected-check">✓</span>}
                  <span>{location}</span>
                </button>
              );
            })}
          </div>
        )}

        <input
          className="event-input-field"
          type="text"
          placeholder="Enter Location"
          value={currentEvent.location}
          onChange={(e) => handleInputChange(e, "location")}
          required
        />
        {errors.location && <p className="error-message">{errors.location}</p>}
      </div>

      <div className="form-section">
        <h4>Event Type</h4>

        {eventTypes && eventTypes.length > 0 && (
          <div className="event-type-buttons-container">
            {eventTypes.map((type) => {
              const isSelected = currentEvent.eventType === type;

              return (
                <button
                  type="button"
                  key={type}
                  className={`event-type-button ${isSelected ? "active" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    handleInputChange({ target: { value: type } }, "eventType")
                  }
                >
                  {isSelected && <span className="selected-check">✓</span>}
                  <span>{type}</span>
                </button>
              );
            })}
          </div>
        )}

        <input
          className="event-input-field"
          type="text"
          placeholder="Enter Event Type"
          value={currentEvent.eventType}
          onChange={(e) => handleInputChange(e, "eventType")}
          required
        />
        {errors.eventType && (
          <p className="error-message">{errors.eventType}</p>
        )}

        {errors.gatheringID && (
          <p className="error-message">{errors.gatheringID}</p>
        )}
      </div>

      {errors.submit && <p className="error-message">{errors.submit}</p>}
      <button
        type="button"
        className="add-event-button"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting
          ? "Saving…"
          : initialEvent
            ? "Update Event"
            : "Add Event"}
      </button>
    </div>
  );
};

export default EventCreationForm;
